import { ESCALA, PALETA_SERIES, deveRotular, formatarValor, gradienteVertical } from "../tema";
import { estiloRotulo, opcoesBase } from "./base";

/**
 * Família 12 — coluna + linha em dois eixos.
 *
 * Duas grandezas de escala diferente no mesmo período: volume exportado
 * (coluna) e preço médio (linha). Num eixo só, a linha de US$ 200 ficaria
 * esmagada no pé das colunas de milhões de toneladas — por isso cada uma
 * tem o próprio eixo, ambos invisíveis.
 *
 * A primeira série é a coluna; as demais viram linhas no segundo eixo. As
 * casas decimais da linha são configuráveis à parte, porque preço e volume
 * raramente usam a mesma precisão.
 */
const modelo = {
  id: "coluna-linha",
  label: "Coluna + linha (dois eixos)",
  descricao: "Volume em colunas e preço em linha, cada um na sua escala. Ex.: exportação x preço.",

  build(spec) {
    const base = opcoesBase(spec);
    const totalCategorias = spec.dados?.categorias?.length || 0;
    const opcoesValor = spec.valores || {};
    const exibirEm = opcoesValor.exibirEm ?? "todos";
    const opcoesLinha = { ...opcoesValor, ...(spec.extras?.valoresLinha || {}) };
    const rotulo = estiloRotulo(spec);
    const eixoOculto = {
      ...base.yAxis,
      min: null,
      max: null,
      visible: false,
    };

    return {
      ...base,
      chart: { ...base.chart, type: "column", marginBottom: (spec.extras?.rodape ? 26 : 19) * ESCALA },
      xAxis: { ...base.xAxis, categories: spec.dados?.categorias || [] },
      yAxis: [
        // A coluna começa no zero (ou no mínimo escolhido) e deixa o terço
        // de cima livre para a linha passar sem cobrir os rótulos.
        { ...eixoOculto, min: spec.eixo?.min ?? 0, maxPadding: 1.05 },
        // A linha flutua no alto: minPadding grande empurra a curva para
        // cima das colunas. Com folgas menores a caixa do ponto mais baixo
        // da linha encostava na caixa da coluna mais alta.
        { ...eixoOculto, opposite: true, minPadding: 1.6, maxPadding: 0.14 },
      ],
      plotOptions: {
        ...base.plotOptions,
        column: {
          borderWidth: 0,
          groupPadding: 0.1,
          pointPadding: 0.04,
          dataLabels: {
            ...rotulo,
            formatter() {
              return formatarValor(this.y, opcoesValor);
            },
          },
        },
        line: {
          lineWidth: 3 * ESCALA,
          marker: { enabled: true, radius: 4 * ESCALA, lineColor: "#FFFFFF", lineWidth: 1.5 * ESCALA },
          dataLabels: {
            ...rotulo,
            y: -6 * ESCALA,
            formatter() {
              return formatarValor(this.y, opcoesLinha);
            },
          },
        },
      },
      series: (spec.dados?.series || []).map((serie, i) => {
        const padrao = PALETA_SERIES[i % PALETA_SERIES.length];
        const coluna = i === 0;
        return {
          name: serie.nome,
          type: coluna ? "column" : "line",
          yAxis: coluna ? 0 : 1,
          zIndex: coluna ? 1 : 2,
          color: coluna
            ? gradienteVertical(serie.cor || padrao.cor, serie.corFim || padrao.corFim)
            : serie.cor || padrao.cor,
          data: (serie.valores || []).map((valor, j) => ({
            y: valor === null ? null : Number(valor),
            dataLabels: { enabled: deveRotular(j, totalCategorias, exibirEm) },
          })),
        };
      }),
    };
  },

  exemplo() {
    return {
      modelo: "coluna-linha",
      titulo: [
        { texto: "Milho", cor: "#F0F000" },
        { texto: " - Exportação x Preço Médio", cor: "#FFFFFF" },
      ],
      subtitulo: [
        { texto: "Volume (mi t) ", cor: "#FFFF0B" },
        { texto: " | ", cor: "#FFFFFF" },
        { texto: " Preço (US$/t)", cor: "#4EA6FF" },
      ],
      fonte: "Fonte: RURAL BUSINESS/Comex Stat",
      fundo: "#000000",
      fundoPlot: "nenhum",
      legenda: { visivel: false },
      dados: {
        categorias: ["mar", "abr", "mai", "jun", "jul", "ago"],
        series: [
          { nome: "Volume", cor: "#FFFF0B", corFim: "#7D8015", valores: [0.4, 0.9, 2.1, 4.8, 7.2, 8.1] },
          { nome: "Preço", cor: "#4EA6FF", valores: [212, 208, 205, 199, 196, 201] },
        ],
      },
      valores: { decimais: 1, prefixo: "", sufixo: "", exibirEm: "todos" },
      rotulo: { corTexto: "#FFFFFF", corFundo: "#1A1A1A", corBorda: "#FFFFFF" },
      extras: { valoresLinha: { decimais: 0 } },
    };
  },
};

export default modelo;
